/**
 * `TrackRow` — one line of the crate. `docs/design-direction.md` §9.3, §8.2.
 *
 * A ledger row, not a card: a 44px `LabelChip`, a one-line title in UI 600, a mono
 * caps meta line, a tabular duration, and a chalk-masked hairline underneath. **Every
 * fourth divider steps up to `--border-strong`** — bar lines, which give the scroll an
 * audible metre with zero decoration. That is the entire ornament budget of the
 * screen the app spends most of its time on.
 *
 * **Zero blend modes** (§8.2, §6.13). A `mix-blend-mode` inside a virtualised row
 * creates a compositing layer per row and is the single easiest way to drop the
 * crate's frame rate on a mid Android; the source guard for this directory asserts
 * that neither this file nor its module contains one.
 *
 * **Omission, never a placeholder** (§12.6). The meta line is assembled from whatever
 * is actually known — performer, year, riddim, BPM — and joined with the one-drop
 * separator. When nothing is known the whole line is absent: no separator hanging in
 * the air, no em-dash standing in for a performer the channel never gave us. The same
 * rule governs the accessible name, which is the title alone when there is no
 * performer to add.
 *
 * **The row is one button and the overflow is another** (§9.3). They are siblings
 * rather than nested, because a button inside a button is invalid HTML that browsers
 * silently un-nest, and because "play this" and "do something to this" are two
 * different actions that must be reachable separately by keyboard and by screen
 * reader. The overflow carries its own Russian name — never an icon with no name, and
 * never an icon-only destructive action (§12.21).
 *
 * The now-playing row is marked three ways at once: `aria-current` for assistive
 * technology, a 3px `--accent` left rule for the eye, and a chip that keeps spinning
 * (§8.2).
 */

import type { JSX } from 'react';

import { hasCyrillic } from '@bl/shared';

import styles from './components.module.css';
import { FileBracket } from './FileBracket';
import { LabelChip } from './LabelChip';
import { formatTimecode } from './Scrub';
import { cx } from './primitives/Rule';

/** The one-drop separator between meta facts. */
const SEP = ' · ';

export const MORE_LABEL = 'Действия с треком';

export interface TrackRowProps {
  readonly title: string;
  /** `null` when genuinely unknown. Never `''`, never a dash (§12.6). */
  readonly performer?: string | null | undefined;
  readonly durationS: number;
  readonly messageId: number;
  readonly seed?: number | undefined;
  /**
   * Extra meta facts in print order — year, riddim, BPM. Anything `null`/`undefined`
   * or blank is dropped; if nothing survives, the second line is not rendered at all.
   */
  readonly meta?: readonly (string | number | null | undefined)[];
  /** The now-playing row (§8.2): `aria-current`, the 3px left rule, a spinning chip. */
  readonly current?: boolean;
  /** Whether the transport is actually running, which is what the chip mirrors. */
  readonly playing?: boolean;
  /** `pending` = queued but not yet downloaded: dashed divider, title at `--text-2`. */
  readonly state?: 'ready' | 'pending';
  /** Absolute index in the ledger, when the caller knows it: drives the bar line. */
  readonly index?: number | undefined;
  readonly onPlay: () => void;
  /**
   * Whether this record is filed (§8.5). Pass BOTH this and {@link onFile} or neither:
   * the bracket renders only when `onFile` is given, and a bracket with no state to
   * show would be a control that lies.
   */
  readonly filed?: boolean | undefined;
  /**
   * Files or unfiles the record. Omit it and no bracket is rendered — the shelf itself
   * and the set list have their own affordances and do not want a second one.
   */
  readonly onFile?: (() => void) | undefined;
  readonly onMore?: (() => void) | undefined;
  readonly moreLabel?: string;
  /** `li` inside a real `<ul>` ledger; `div` inside a windowing container. */
  readonly as?: 'li' | 'div';
  readonly className?: string | undefined;
}

export function TrackRow({
  title,
  performer = null,
  durationS,
  messageId,
  seed,
  meta = [],
  current = false,
  playing = false,
  state = 'ready',
  index,
  onPlay,
  filed = false,
  onFile,
  onMore,
  moreLabel = MORE_LABEL,
  as = 'div',
  className,
}: TrackRowProps): JSX.Element {
  const facts = [performer, ...meta]
    .map((fact) => (typeof fact === 'number' ? String(fact) : (fact ?? '')))
    .map((fact) => fact.trim())
    .filter((fact) => fact !== '');

  const metaLine = facts.join(SEP);
  const hasMeta = metaLine !== '';

  /* The accessible name: the title, plus the performer when there is one. A comma,
     because a dash here would be the placeholder §12.6 bans, read aloud. */
  const name =
    facts.length > 0 && performer !== null && performer !== undefined && performer !== ''
      ? `${title}, ${performer}`
      : title;

  const barline = index !== undefined && (index + 1) % 4 === 0;
  const Tag = as;

  return (
    <Tag
      className={cx(styles.row, barline && styles.barline, className)}
      data-state={state}
      data-current={current ? 'true' : 'false'}
      data-barline={barline ? 'true' : undefined}
    >
      <button
        type="button"
        className={styles.main}
        aria-label={name}
        aria-current={current ? true : undefined}
        onClick={onPlay}
      >
        <LabelChip
          title={title}
          performer={performer}
          messageId={messageId}
          seed={seed}
          size={44}
          spinning={current && playing}
        />

        <span className={styles.rowLines}>
          <span className={cx('t-ui', 't-17', 't-clamp-1', styles.rowTitle)}>{title}</span>
          {hasMeta ? (
            <span
              className={cx(
                't-mono',
                't-11',
                't-clamp-1',
                styles.rowMeta,
                hasCyrillic(metaLine) && styles.cyr,
              )}
            >
              {metaLine}
            </span>
          ) : null}
        </span>

        <span className={cx('t-mono', 't-13', styles.duration)}>{formatTimecode(durationS)}</span>
      </button>

      {onFile === undefined ? null : <FileBracket filed={filed} onToggle={onFile} />}

      {onMore === undefined ? null : (
        <button type="button" className={styles.more} aria-label={moreLabel} onClick={onMore}>
          <span className={styles.moreGlyph} aria-hidden="true" />
        </button>
      )}
    </Tag>
  );
}
